import React, { Fragment, useRef, useState, useEffect } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';

import * as ApiTimeClock from '../../api/ApiTimeClock';
import { ERRORS } from '../../constants/APDConstants'
import * as AlertMSGConstants from '../../constants/AlertMSGConstants';
import UseAlertMessageState from '../../control/alert/UseAlertMessageState';
import UseLoadingState from '../../control/loading/UseLoadingState';
import AlertMessage from '../../control/alert/AlertMessage';
import Loading from '../../control/loading/Loading';
import Button from '../../control/button/Button'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '24px',
  },
  rootPaper: {
    width: '100%',
    ...theme.paperPadding,
  },
  divider: {
    backgroundColor: theme.mainTheme.color1,
  },
  labelStyle: {
    paddingTop: 24,
    color: theme.mainTheme.color1,
  },
  labelRequiredStyle: {
    paddingLeft: 4,
    color: theme.mainTheme.color5,
  },
  normalTextFields: {
    '& input': {
      height: theme.field.height,
      padding: theme.field.padding,
      fontSize: theme.fontSize.body1
    }
  },
  fileFields: {
    '& input': {
      height: theme.field.height,
      padding: theme.field.padding,
      fontSize: theme.fontSize.body1,
      alignContent: 'center'
    }
  },
  footer: {
    paddingTop: 24,
  },
  btnCancel: {
    backgroundColor: '#808080 !important'
  }
}));

const MeaBoundary = () => {
  const inputFileRef = useRef(null);
  const inputBufferRef = useRef(null);
  const classes = useStyles();
  const alertMessageState = UseAlertMessageState();
  const loadingState = UseLoadingState();
  const [currentValue, setCurrentValue] = useState({
    boundaryFileName: null,
    buffer: null,
  });
  const [number, setNumber] = useState("");
  const [file, setFile] = useState(null);
  const [numberError, setNumberError] = useState("");
  const [fileError, setFileError] = useState("");

  const fetchData = async () => {
    try {
      loadingState.showLoading();
      const response = await ApiTimeClock.fetchMeaBoundary();
      if (response && response.data) {
        setCurrentValue({
          boundaryFileName: response.data.boundaryFileName,
          buffer: response.data.buffer,
        });
        inputFileRef.current.value = null;
        setFile(null);
        setFileError("");
        setNumber(response.data.buffer);
        setNumberError("");
      } else {
        alertMessageState.showAlert({
          altMsg: AlertMSGConstants.SYSTEM_ERROR,
        });
      }
    } finally {
      loadingState.hideLoading();
    }
  };

  const save = async () => {
    try {
      loadingState.showLoading();
      const formData = new FormData();
      formData.append('boundaryFile', file);
      formData.append('buffer', number);
      const response = await ApiTimeClock.updateMeaBoundary(formData);
      if (response && response.data && response.data.status === "success") {
        alertMessageState.showAlert({
          altMsg: 'บันทึกข้อมูลสำเร็จ',
          callbackCancel: () => {
            fetchData()
            alertMessageState.hideAlert()
          }
        })
      } else {
        alertMessageState.showAlert({
          altMsg: 'บันทึกข้อมูลไม่สำเร็จ',
        });
      }
    } finally {
      loadingState.hideLoading();
    }
  };

  const handleFileChange = (event) => validateFile(event.target.files[0]);

  const handleNumberChange = (event) => validateBuffer(event.target.value);

  const validateFile = (file) => {
    if (!file) {
      setFile(null);
      setFileError("");
      return true
    } else if (file && file.name.endsWith(".geojson")) {
      setFile(file);
      setFileError("");
      return true
    } else {
      setFile(null);
      setFileError("รองรับไฟล์นามสกุล .geojson เท่านั้น");
      return false
    }
  }

  const validateBuffer = (value) => {
    if (value === "" || value === null) {
      setNumber(null);
      setNumberError(ERRORS.REQUIRED);
      return false
    } else if (value >= 0 && value <= 10000) {
      const intValue = parseInt(value);
      inputBufferRef.current.value = intValue;
      setNumber(intValue);
      setNumberError("");
      return true
    } else {
      setNumber(null);
      setNumberError("ขนาด Buffer ต้องอยู่ระหว่าง 0 ถึง 10,000");
      return false
    }
  }

  const handleClickReset = () => {
    fetchData();
  };

  const handleClickSave = () => {
    if (validateFile(file) && validateBuffer(inputBufferRef.current.value)) {
      alertMessageState.showAlert({
        altMsg: 'คุณต้องการยืนยันการบันทึกข้อมูลนี้ใช่หรือไม่',
        altType: 2,
        callbackCancel: () => alertMessageState.hideAlert(),
        callbackConfirm: () => {
          save()
          alertMessageState.hideAlert()
        }
      })
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Fragment>
      <Scrollbars universal={true}>
        <Grid container className={classes.root}>
          <Paper className={classes.rootPaper}>
            <Typography variant="h5">ข้อมูลขอบเขตพื้นที่ กฟน.</Typography>
            <Divider className={classes.divider} />
            <Typography className={classes.labelStyle}>
              {'อัปโหลดไฟล์ขอบเขตพื้นที่ (.geojson)'}
            </Typography>
            <TextField
              className={classes.fileFields}
              type="file"
              onChange={handleFileChange}
              variant="outlined"
              fullWidth
              error={!!fileError}
              helperText={fileError}
              inputProps={{
                ref: inputFileRef,
                accept: ".geojson",
              }}
            />
            {currentValue.boundaryFileName && (
              <Typography>ไฟล์ขอบเขตพื้นที่ปัจจุบัน: {currentValue.boundaryFileName}</Typography>
            )}
            <Typography className={classes.labelStyle}>
              {'กำหนดขนาด Buffer พื้นที่ (เมตร)'}
              <span className={classes.labelRequiredStyle}>*</span>
            </Typography>
            <TextField
              className={classes.normalTextFields}
              type="number"
              value={number}
              onChange={handleNumberChange}
              variant="outlined"
              inputProps={{
                ref: inputBufferRef,
                min: 0,
                max: 10000
              }}
              fullWidth
              required
              error={!!numberError}
              helperText={numberError}
            />
            <Grid className={classes.footer} container justify='center' spacing={2}>
              <Grid item>
                <Button
                  label='ยกเลิก'
                  className={classes.btnCancel}
                  onClick={handleClickReset}
                />
              </Grid>
              <Grid item>
                <Button
                  label='บันทึก'
                  onClick={handleClickSave}
                  disabled={!file}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Scrollbars>
      <AlertMessage {...alertMessageState.altProps} />
      <Loading isShowLoading={loadingState.isShowLoading} />
    </Fragment>
  );
};

export default MeaBoundary;
